import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaDownload, FaFileAlt, FaBook, FaChartLine, FaTimes, FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./Pages.css";

const Resources = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    resourceTitle: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resources = [
    {
      title: "Why ThinkRise USA Overview",
      description: "Company overview with delivery model, differentiators, and core outcomes.",
      icon: <FaFileAlt />,
      type: "DOC",
      size: "Guide",
      url: "/downloads/thinkrise-overview.pdf", // Assuming paths
    },
    {
      title: "Solutions and Services",
      description: "Service architecture for data, campaigns, operations, and automation.",
      icon: <FaBook />,
      type: "DOC",
      size: "Guide",
      url: "/downloads/solutions-services.pdf",
    },
    {
      title: "Products and Industries",
      description: "Industry-specific product packages and use cases.",
      icon: <FaChartLine />,
      type: "DOC",
      size: "Brief",
      url: "/downloads/products-industries.pdf",
    },
    {
      title: "Target Conference Calendar 2026",
      description: "Monthly conference targeting tracker for outreach planning.",
      icon: <FaFileAlt />,
      type: "XLSX",
      size: "Template",
      url: "/downloads/conference-calendar.xlsx",
    },
  ];

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setFormData({ ...formData, resourceTitle: resource.title });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
    setFormData({ name: "", email: "", company: "", resourceTitle: "" });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("company", formData.company);
    formDataToSend.append("resource", formData.resourceTitle);
    formDataToSend.append("_subject", `Resource Download Request: ${formData.resourceTitle}`);

    try {
      const response = await fetch("https://formspree.io/f/mpqjboey", {
        method: "POST",
        body: formDataToSend,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setIsSubmitted(true);
        // In a real app, you might trigger the download here
        // window.open(selectedResource.url, '_blank');
      } else {
        alert("There was an error submitting the form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      <Helmet>
        <title>Resources | ThinkRise USA</title>
        <meta
          name="description"
          content="Download ThinkRise USA resources, guides, templates, and planning assets to help your team execute campaigns, operations, and growth programs."
        />
      </Helmet>
      <section className="page-hero resources-hero">
        <div className="container">
          <h1 className="page-hero-title">ThinkRise USA Resources</h1>
          <p className="page-hero-subtitle">
            Download practical assets designed to help your team plan, execute, and scale growth.
          </p>
        </div>
      </section>

      <section className="section resources-section">
        <div className="container">
          <h2 className="section-title">Resource Library</h2>
          <div className="resources-grid">
            {resources.map((resource) => (
              <article key={resource.title} className="resource-card">
                <div className="resource-icon">{resource.icon}</div>
                <div className="resource-content">
                  <div className="resource-header">
                    <h3>{resource.title}</h3>
                    <span className="resource-type">{resource.type}</span>
                  </div>
                  <p className="resource-description">{resource.description}</p>
                  <div className="resource-meta">{resource.size}</div>
                  <button onClick={() => handleDownloadClick(resource)} className="btn resource-download-btn">
                    <FaDownload className="btn-icon" /> Download
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="resources-cta">
            <h3>Need a tailored growth blueprint?</h3>
            <p>Schedule a consultation and we will map a plan to your goals.</p>
            <Link to="/contact" className="btn cta-btn">Book Consultation</Link>
          </div>
        </div>
      </section>

      {/* Modal Section */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <FaTimes />
            </button>

            {isSubmitted ? (
              <div className="modal-success-state">
                <FaCheckCircle className="success-icon animated-jump" />
                <h2>Request Received!</h2>
                <p>Thank you for your interest in <strong>{selectedResource?.title}</strong>.</p>
                <p>We've sent the details to your email ({formData.email}).</p>
                <button className="btn close-btn" onClick={closeModal}>Close</button>
              </div>
            ) : (
              <div className="modal-form-state">
                <div className="modal-header">
                  <div className="modal-logo-container">
                    <img src="/thinkrise_logo.jpeg" alt="ThinkRise USA Logo" className="modal-logo" />
                  </div>
                  <h2>Download Resource</h2>
                  <p>Complete the form to get access to <strong>{selectedResource?.title}</strong></p>
                </div>

                <form onSubmit={handleSubmit} className="resource-request-form">
                  <div className="form-group slide-up-1">
                    <label htmlFor="name">Full Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" />
                  </div>

                  <div className="form-group slide-up-2">
                    <label htmlFor="email">Work Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@company.com" />
                  </div>

                  <div className="form-group slide-up-3">
                    <label htmlFor="company">Company Name</label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Company Ltd." />
                  </div>

                  <button type="submit" className="btn submit-btn slide-up-4" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <><span className="spinner"></span> Processing...</>
                    ) : (
                      <><FaPaperPlane style={{ marginRight: "8px" }} /> Request Download</>
                    )}
                  </button>
                  <p className="form-note slide-up-5">By clicking submit, you agree to receive the requested file via email.</p>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default Resources;

